import { useEffect, useRef } from 'react'
import type { MapRef } from 'react-map-gl'

const SHAPE_ICONS = [
  { name: 'amber-lease', url: '/markers/amber-lease.png' },
  { name: 'amber-sale', url: '/markers/amber-sale.png' },
  { name: 'amber-sale-lease', url: '/markers/amber-sale-lease.png' },
  { name: 'cyan-lease', url: '/markers/cyan-lease.png' },
  { name: 'cyan-sale', url: '/markers/cyan-sale.png' },
  { name: 'cyan-sale-lease', url: '/markers/cyan-sale-lease.png' },
  { name: 'emerald-lease', url: '/markers/emerald-lease.png' },
  { name: 'emerald-sale', url: '/markers/emerald-sale.png' },
  { name: 'emerald-sale-lease', url: '/markers/emerald-sale-lease.png' },
  { name: 'featured', url: '/markers/featured.png' },
  { name: 'featured-selected', url: '/markers/featured-selected.png' },
  { name: 'fuchsia-lease', url: '/markers/fuchsia-lease.png' },
  { name: 'fuchsia-sale', url: '/markers/fuchsia-sale.png' },
  { name: 'fuchsia-sale-lease', url: '/markers/fuchsia-sale-lease.png' },
  { name: 'indigo-lease', url: '/markers/indigo-lease.png' },
  { name: 'indigo-sale', url: '/markers/indigo-sale.png' },
  { name: 'indigo-sale-lease', url: '/markers/indigo-sale-lease.png' },
  { name: 'orange-dark-business', url: '/markers/orange-dark-business.png' },
  { name: 'orange-lease', url: '/markers/orange-lease.png' },
  { name: 'orange-sale', url: '/markers/orange-sale.png' },
  { name: 'orange-sale-lease', url: '/markers/orange-sale-lease.png' },
  { name: 'pink-lease', url: '/markers/pink-lease.png' },
  { name: 'pink-sale', url: '/markers/pink-sale.png' },
  { name: 'pink-sale-lease', url: '/markers/pink-sale-lease.png' },
  { name: 'red-lease', url: '/markers/red-lease.png' },
  { name: 'red-sale', url: '/markers/red-sale.png' },
  { name: 'red-sale-lease', url: '/markers/red-sale-lease.png' },
  { name: 'rose-lease', url: '/markers/rose-lease.png' },
  { name: 'selected-business', url: '/markers/selected-business.png' },
  { name: 'selected-lease', url: '/markers/selected-lease.png' },
  { name: 'selected-sale', url: '/markers/selected-sale.png' },
  { name: 'selected-sale-lease', url: '/markers/selected-sale-lease.png' },
  { name: 'slate-lease', url: '/markers/slate-lease.png' },
  { name: 'slate-sale', url: '/markers/slate-sale.png' },
  { name: 'slate-sale-lease', url: '/markers/slate-sale-lease.png' },
  { name: 'stone-lease', url: '/markers/stone-lease.png' },
  { name: 'stone-sale', url: '/markers/stone-sale.png' },
  { name: 'stone-sale-lease', url: '/markers/stone-sale-lease.png' },
  { name: 'yellow-lease', url: '/markers/yellow-lease.png' },
  { name: 'yellow-sale', url: '/markers/yellow-sale.png' },
  { name: 'yellow-sale-lease', url: '/markers/yellow-sale-lease.png' },
]

const ICON_URLS = new Map(SHAPE_ICONS.map((icon) => [icon.name, icon.url]))

export function useMapIcons(
  mapRef: React.RefObject<MapRef | null> | null,
  reattachKey?: unknown
) {
  const loadingIconsByMapRef = useRef(new WeakMap<object, Set<string>>())

  useEffect(() => {
    if (!mapRef) {
      return
    }

    let cleanup: (() => void) | null = null
    let frameId: number | null = null

    const attach = () => {
      const map = mapRef.current?.getMap()
      if (!map) {
        frameId = window.requestAnimationFrame(attach)
        return
      }

      const loadingIcons =
        loadingIconsByMapRef.current.get(map) ?? new Set<string>()
      loadingIconsByMapRef.current.set(map, loadingIcons)

      const addIcon = (name: string) => {
        const url = ICON_URLS.get(name)
        if (!url || map.hasImage(name) || loadingIcons.has(name)) return

        loadingIcons.add(name)

        map.loadImage(url, (error, image) => {
          loadingIcons.delete(name)

          if (error || !image || map.hasImage(name)) {
            return
          }

          try {
            map.addImage(name, image, { sdf: false, pixelRatio: 1 })
          } catch {
            // Style may switch before async image load finishes.
          }
        })
      }

      const registerIcons = () => {
        if (!map.isStyleLoaded()) return
        SHAPE_ICONS.forEach(({ name }) => addIcon(name))
      }

      const handleStyleImageMissing = (e: { id?: string }) => {
        if (!e?.id || !map.isStyleLoaded()) return
        addIcon(e.id)
      }

      registerIcons()

      map.on('load', registerIcons)
      map.on('style.load', registerIcons)
      map.on('styleimagemissing', handleStyleImageMissing)

      cleanup = () => {
        map.off('load', registerIcons)
        map.off('style.load', registerIcons)
        map.off('styleimagemissing', handleStyleImageMissing)
      }
    }

    attach()

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
      cleanup?.()
    }
  }, [mapRef, reattachKey])
}
